import dayjs from "dayjs";
import {
  type FC,
  type ReactNode,
  type RefObject,
  useCallback,
  useState,
} from "react";
import { ButtonUI } from "../../components/Button";
import { RelativeTimeDisplay } from "../../components/RelativeTimeDisplay/RelativeTimeDisplay";
import {
  genderMap,
  hrMap,
  industryCategoryMap,
  remoteFrequencyMap,
  TECH_ROW_LIMIT,
} from "../../constants/email-card";
import { searchGmail } from "../../utils/gmail-utils";
import {
  convertEnToJa,
  getValidString,
  getValidStringArray,
} from "../../utils/text-utils";
import { ReactComponent as ArrowDownIcon } from "./arrowDown.svg";
import { ReactComponent as ArrowRightIcon } from "./arrowRight.svg";
import { ReactComponent as ArrowUpIcon } from "./arrowUp.svg";
import { ReactComponent as BuildingIcon } from "./building.svg";
import { ReactComponent as CalendarIcon } from "./calendar.svg";
import { ReactComponent as GmailIcon } from "./gmail.svg";
import { ReactComponent as LocationIcon } from "./location.svg";
import { ReactComponent as PaperclipIcon } from "./paperclip.svg";
import { ReactComponent as RewardIcon } from "./reward.svg";
import { ReactComponent as TechStackIcon } from "./techStack.svg";

export type CardIndustryCategoryJaType =
  | "金融"
  | "保険"
  | "官公庁"
  | "製造業"
  | "医療"
  | "教育"
  | "小売"
  | "スタートアップ";

export type CardRemoteFrequencyJaType =
  | "フルリモート"
  | "リモート併用"
  | "常駐勤務";

export type CardGenderType = "男性" | "女性" | "不明";
export type CardHRType = "正社員" | "契約社員" | "フリーランス" | "不明";

export type EmailCardMainInfo = {
  icon: ReactNode;
  label: string;
  value: ReactNode;
};

export type EmailCardBadge = {
  label: string;
  colorClass: string;
};

export type EmailCardProps = {
  isMatter?: boolean;
  emailId: string;
  onSelectEmail?: (emailId: string) => void;
  title: ReactNode;
  date: string;
  period: string | null;
  content: string;
  reward: string | number | null;
  isRemoteEnabled: boolean;
  station: string | null;
  numberOfInterviews?: string | null;
  otherCountriesEnabled?: boolean;
  onlyEnableFreelance?: boolean | null;
  workingTimeWidth?: string | null;
  endOrPrimeContractorDirect?: string | null;
  depth?: number | null;
  technology: string[] | null;
  industryCategory?: string | null;
  remoteFrequency?: string | null;
  description?: ReactNode;
  isRead?: boolean;
  matchScore?: number | null;
  age?: string | null;
  gender?: string | null;
  hrTypes?: string | null;
  hasAttachments?: boolean;
  duplicateCount?: number;
  originalMessageId?: string | null;
  scrollCardRef: RefObject<HTMLDivElement> | null;
  scrollCardId: string | null;
};

export const EmailCard: FC<EmailCardProps> = ({
  isMatter = false,
  emailId,
  onSelectEmail = null,
  title,
  date,
  period,
  content,
  reward,
  isRemoteEnabled,
  station,
  numberOfInterviews = null,
  otherCountriesEnabled = null,
  onlyEnableFreelance = null,
  workingTimeWidth = null,
  endOrPrimeContractorDirect = null,
  depth = null,
  technology = null,
  industryCategory = null,
  remoteFrequency = null,
  description = null,
  isRead = null,
  matchScore = null,
  age = null,
  gender = null,
  hrTypes = null,
  hasAttachments = false,
  duplicateCount,
  originalMessageId = null,
  scrollCardRef,
  scrollCardId,
}) => {
  const rewardFromContentToValue = useCallback(
    (content: string): string | null => {
      const rewardFromContent = content.match(/([0-9０-９,，]+)[\s]*(?:万)?円/);

      if (!rewardFromContent) {
        return null;
      }

      if (
        (rewardFromContent?.[0]?.length <= 5 &&
          rewardFromContent?.[0].endsWith("万円")) ||
        rewardFromContent?.[0].endsWith("万")
      ) {
        return `〜${rewardFromContent[0]}/月`;
      } else if (rewardFromContent?.[1]?.length > 5) {
        return `〜${rewardFromContent[1]}円/月`;
      } else if (rewardFromContent?.[1]) {
        return `〜${rewardFromContent[1]}万円/月`;
      }
      return null;
    },
    [],
  );

  const validPeriod = getValidString(period);
  const validStation = getValidString(station);
  const remoteFrequencyJa = convertEnToJa(remoteFrequency, remoteFrequencyMap);
  const mainInfos: EmailCardMainInfo[] = [
    {
      icon: <CalendarIcon className="w-[1.4rem] h-[1.4rem]" />,
      label: isMatter ? "期間" : "稼働時期",
      value: validPeriod || "要確認",
    },
    {
      icon: <RewardIcon className="w-[1.4rem] h-[1.4rem]" />,
      label: isMatter ? "単価（税別）" : "希望単価（税別）",
      value: (() => {
        const rewardNum = reward != null ? Number(reward) : NaN;
        if (!isNaN(rewardNum)) {
          return `〜${rewardNum.toLocaleString()}円/月`;
        }
        const rewardFromContent = rewardFromContentToValue(content);
        if (rewardFromContent != null) {
          return rewardFromContent;
        }
        return "要相談/月";
      })(),
    },
    {
      icon: <LocationIcon className="w-[1.4rem] h-[1.4rem]" />,
      label: isMatter ? "勤務場所" : "最寄駅",
      value: validStation ? validStation : "要確認",
    },
    {
      icon: <BuildingIcon className="w-[1.4rem] h-[1.4rem]" />,
      label: isMatter ? "勤務形態" : "希望勤務形態",
      value: isMatter
        ? remoteFrequencyJa
          ? remoteFrequencyJa
          : isRemoteEnabled
            ? "リモート可"
            : "要確認"
        : remoteFrequencyJa
          ? remoteFrequencyJa
          : isRemoteEnabled
            ? "リモート希望あり"
            : "要確認",
    },
  ];

  const genderJa = convertEnToJa(gender, genderMap);
  const hrTypesJa = convertEnToJa(hrTypes, hrMap);
  const industryCategoryJa = convertEnToJa(
    industryCategory,
    industryCategoryMap,
  );
  const badges: (EmailCardBadge | null)[] = [
    getValidString(numberOfInterviews) != null
      ? {
          label: `面談回数：${numberOfInterviews}回`,
          colorClass: "bg-blue-100 text-bkack",
        }
      : null,
    getValidString(workingTimeWidth) != null
      ? {
          label: `精算幅：${workingTimeWidth}`,
          colorClass: "bg-blue-100 text-bkack",
        }
      : null,
    depth != null
      ? {
          label: isMatter
            ? `商流：${depth}次請け`
            : `商流：${depth === 0 ? "自社所属" : depth === 1 ? "直所属" : `${depth - 1}社先`}`,
          colorClass:
            depth <= 1
              ? "bg-green-100 text-green-800"
              : depth === 2
                ? "bg-blue-100 text-blue-800"
                : depth === 3
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-red-100 text-red-800",
        }
      : getValidString(endOrPrimeContractorDirect) != null
        ? {
            label:
              endOrPrimeContractorDirect === "prime"
                ? "元請直/エンド直：元請直"
                : endOrPrimeContractorDirect === "direct"
                  ? "元請直/エンド直：エンド直"
                  : "元請直/エンド直：不明",
            colorClass: "bg-blue-100 text-bkack",
          }
        : null,
    otherCountriesEnabled === true
      ? {
          label: "外国籍OK",
          colorClass: "bg-green-100 text-bkack",
        }
      : null,
    onlyEnableFreelance === true
      ? {
          label: "フリーランスOK",
          colorClass: "bg-yellow-100 text-bkack",
        }
      : null,
    getValidString(age) != null
      ? {
          label: `年齢：${age}歳`,
          colorClass: "bg-blue-100 text-bkack",
        }
      : null,
    getValidString(gender) != null
      ? {
          label: `性別：${genderJa}`,
          colorClass: "bg-blue-100 text-bkack",
        }
      : null,
    getValidString(hrTypes) != null
      ? {
          label: `人材種別：${hrTypesJa}`,
          colorClass: "bg-yellow-100 text-bkack",
        }
      : null,
    industryCategoryJa != null
      ? {
          label: `${!isMatter ? "経験" : ""}業種：${industryCategoryJa}`,
          colorClass: "bg-yellow-100 text-bkack",
        }
      : null,
  ].filter(Boolean);

  const validTechnology = getValidStringArray(technology);
  const [isTechExpanded, setIsTechExpanded] = useState(
    validTechnology?.length ? validTechnology.length <= TECH_ROW_LIMIT : true,
  );

  return (
    <div
      id={`email-card-${emailId}`}
      className={`border border-gray-300 ${
        !isRead ? "bg-white shadow-lg" : "bg-gray-100 shadow-md"
      } rounded-lg hover:shadow-lg transition-shadow duration-200`}
    >
      {/* スクロール復元対象のカードの場合divを追加 */}
      {scrollCardId === emailId && <div ref={scrollCardRef} />}
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {/* Newバッジ */}
            {date && dayjs(date).diff(dayjs().startOf("day")) > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-orange-100 text-orange-800">
                New
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* 受信日 */}
            {date && (
              <RelativeTimeDisplay
                date={date}
                className="text-sm text-gray-600"
              />
            )}
            {/* マッチ度スコア */}
            {matchScore && (
              <div
                className={`px-3 py-2 rounded-full text-sm font-bold flex items-center space-x-1 ${
                  matchScore >= 80
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : matchScore >= 60
                      ? "bg-yellow-100 text-yellow-800 border border-yellow-200"
                      : "bg-red-100 text-red-800 border border-red-200"
                }`}
              >
                <span className="text-lg font-bold">{matchScore}%</span>
                <span className="text-xs font-medium">マッチ</span>
              </div>
            )}
          </div>
        </div>
        {/* タイトル */}
        <h3
          className={`text-lg text-blue-600 mb-3 line-clamp-2 flex items-start gap-1.5 ${
            !isRead ? "font-extrabold" : "font-normal"
          }`}
        >
          {hasAttachments && (
            <PaperclipIcon
              className="w-5 h-5 text-gray-400 shrink-0 mt-0.5"
              title="添付ファイルあり"
            />
          )}
          {duplicateCount != null && duplicateCount >= 2 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200 shrink-0 mt-0.5">
              重複 {duplicateCount}件
            </span>
          )}
          <span>{title}</span>
        </h3>
        {/* 情報アイテム */}
        {mainInfos && (
          <div className="space-y-2 mb-2">
            {mainInfos.map(info => (
              <div className="flex items-center" key={info.label}>
                <span className="text-gray-700 mr-2">{info.icon}</span>
                <div className="flex-1">
                  <span className="text-sm text-gray-700 font-medium mr-3">
                    {info.label}
                  </span>
                  <span className="text-sm text-gray-600">{info.value}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* スキル情報エリア */}
        {Array.isArray(validTechnology) && validTechnology.length > 0 && (
          <div className="mb-3">
            <div className="flex items-start mb-2">
              <div className="flex items-center">
                <span className="text-gray-700 mr-2">
                  <TechStackIcon className="w-[1.4rem] h-[1.4rem]" />
                </span>
                <div className="min-w-[4.5rem] mr-3">
                  <span className="text-sm text-gray-700 font-medium">
                    {isMatter ? "必要スキル" : "保有スキル"}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(isTechExpanded
                  ? validTechnology
                  : validTechnology.slice(0, TECH_ROW_LIMIT)
                ).map(tech => (
                  <span
                    key={tech}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-black"
                  >
                    {tech}
                  </span>
                ))}
                {validTechnology.length > TECH_ROW_LIMIT && !isTechExpanded && (
                  <ButtonUI
                    type="button"
                    size="xs"
                    color="normal"
                    className="!text-gray-600 hover:!bg-gray-200"
                    isOutline={true}
                    onClick={() => setIsTechExpanded(true)}
                  >
                    すべて表示
                    <ArrowDownIcon className="w-[1rem] h-[1rem]" />
                  </ButtonUI>
                )}
                {validTechnology.length > TECH_ROW_LIMIT && isTechExpanded && (
                  <ButtonUI
                    type="button"
                    size="xs"
                    color="normal"
                    className="!text-gray-600 hover:!bg-gray-200"
                    isOutline={true}
                    onClick={() => setIsTechExpanded(false)}
                  >
                    閉じる
                    <ArrowUpIcon className="w-[1rem] h-[1rem]" />
                  </ButtonUI>
                )}
              </div>
            </div>
          </div>
        )}
        {/* バッジ */}
        {badges && badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {badges.map((badge, idx) => (
              <span
                key={`${badge?.label}-${idx}`}
                className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${badge?.colorClass}`}
              >
                {badge?.label}
              </span>
            ))}
          </div>
        )}
        {/* 説明 */}
        {description && (
          <div className="border-t border-gray-200 pt-3 mb-3">
            {description}
          </div>
        )}
        {/* フッター（詳細を見るボタンエリア） */}
        {
          <div className="flex items-center justify-end gap-2">
            {
              <GmailIcon
                onClick={() =>
                  searchGmail(String(title ?? ""), originalMessageId)
                }
                className="w-8 h-8 p-[0.2rem] cursor-pointer rounded hover:bg-gray-200 active:bg-gray-300 text-gray-700"
              />
            }

            {!emailId.startsWith("fallback-") &&
            !emailId.startsWith("error-") ? (
              <ButtonUI
                to={`/emails/search/${emailId}`}
                size="sm"
                color="primary"
                onClick={
                  onSelectEmail ? () => onSelectEmail(emailId) : undefined
                }
              >
                詳細を見る
                <ArrowRightIcon className="ml-2 -mr-1 w-4 h-4" />
              </ButtonUI>
            ) : (
              <ButtonUI
                size="sm"
                color="normal"
                className="cursor-not-allowed pointer-events-none"
                disabled={true}
              >
                詳細を見る
                <ArrowRightIcon className="ml-2 -mr-1 w-4 h-4" />
              </ButtonUI>
            )}
          </div>
        }
      </div>
    </div>
  );
};
